import { moveSlidingToggle } from './sliding-toggle.js';
import {
  closeQuickBuyDropdowns,
  disposeQuickBuyCards,
  initializeQuickBuyCard,
} from './quick-buy-card.js';

const instances = new WeakSet();
const cleanupByRoot = new WeakMap();

const initializeRail = (panel) => {
  const track = panel.querySelector('[data-quick-buy-track]');
  const controls = panel.querySelector('[data-quick-buy-controls]');
  const previous = panel.querySelector('[data-quick-buy-previous]');
  const next = panel.querySelector('[data-quick-buy-next]');
  if (!track || !controls || !previous || !next) return () => {};
  const reducedMotion = matchMedia('(prefers-reduced-motion: reduce)');
  let frame;
  const update = () => {
    frame = 0;
    const max = track.scrollWidth - track.clientWidth;
    controls.hidden = max <= 2;
    previous.disabled = track.scrollLeft <= 2;
    next.disabled = track.scrollLeft >= max - 2;
  };
  const scheduleUpdate = () => { if (!frame) frame = requestAnimationFrame(update); };
  const scroll = (direction) => {
    const cards = [...track.querySelectorAll('[data-quick-buy-card]')];
    const start = cards[0]?.offsetLeft || 0;
    const current = track.scrollLeft;
    const position = direction > 0
      ? cards.find((card) => card.offsetLeft - start > current + 2)
      : [...cards].reverse().find((card) => card.offsetLeft - start < current - 2);
    const max = track.scrollWidth - track.clientWidth;
    track.scrollTo({ left: position ? position.offsetLeft - start : direction > 0 ? max : 0,
      behavior: reducedMotion.matches ? 'instant' : 'smooth' });
  };
  previous.addEventListener('click', () => scroll(-1));
  next.addEventListener('click', () => scroll(1));
  const handleScroll = () => {
    closeQuickBuyDropdowns(panel);
    scheduleUpdate();
  };
  track.addEventListener('scroll', handleScroll, { passive: true });
  track.addEventListener('keydown', (event) => {
    if (event.target !== track) return;
    if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
      event.preventDefault();
      scroll(event.key === 'ArrowLeft' ? -1 : 1);
    }
  });
  const resize = new ResizeObserver(() => {
    closeQuickBuyDropdowns(panel);
    scheduleUpdate();
  });
  resize.observe(track);
  scheduleUpdate();
  return () => { resize.disconnect(); cancelAnimationFrame(frame); };
};

const activateTab = (root, nextTab, focus = false, animate = false) => {
  const tabs = [...root.querySelectorAll('[data-quick-buy-tab]')];
  const panels = [...root.querySelectorAll('[data-quick-buy-panel]')];
  const name = nextTab.dataset.quickBuyTab;

  moveSlidingToggle(root.querySelector('.quick-buy__tab-indicator'), tabs.indexOf(nextTab));
  closeQuickBuyDropdowns(root);
  root.dataset.panelMotion = String(animate);

  tabs.forEach((tab) => {
    const active = tab === nextTab;
    tab.setAttribute('aria-selected', String(active));
    tab.tabIndex = active ? 0 : -1;
  });

  panels.forEach((panel) => {
    const active = panel.dataset.quickBuyPanel === name;
    panel.hidden = !active;
    panel.inert = !active;
    panel.setAttribute('aria-hidden', String(!active));
  });

  root.dataset.activeTab = name;
  if (focus) nextTab.focus();
};

const initialize = (root) => {
  if (instances.has(root)) return;
  instances.add(root);

  const tablist = root.querySelector('[data-quick-buy-tabs]');
  const tabs = [...root.querySelectorAll('[data-quick-buy-tab]')];
  const abortController = new AbortController();
  const { signal } = abortController;

  root.classList.add('is-enhanced');
  if (tablist) tablist.hidden = false;

  tabs.forEach((tab, index) => {
    tab.addEventListener('click', (event) => activateTab(root, tab, false, event.detail > 0), { signal });
    tab.addEventListener('keydown', (event) => {
      let nextIndex = index;

      if (event.key === 'ArrowRight') nextIndex = (index + 1) % tabs.length;
      else if (event.key === 'ArrowLeft') nextIndex = (index - 1 + tabs.length) % tabs.length;
      else if (event.key === 'Home') nextIndex = 0;
      else if (event.key === 'End') nextIndex = tabs.length - 1;
      else return;

      event.preventDefault();
      activateTab(root, tabs[nextIndex], true);
    }, { signal });
  });

  const cleanup = [...root.querySelectorAll('[data-quick-buy-card]')].map(initializeQuickBuyCard);
  cleanup.push(...[...root.querySelectorAll('[data-quick-buy-panel]')].map(initializeRail));
  cleanupByRoot.set(root, () => {
    closeQuickBuyDropdowns(root);
    abortController.abort();
    cleanup.forEach((dispose) => dispose());
  });
  const selectedTab = tabs.find((tab) => tab.getAttribute('aria-selected') === 'true') || tabs[0];
  if (selectedTab) activateTab(root, selectedTab);
};

window.addEventListener('resize', () => closeQuickBuyDropdowns(document), { passive: true });
window.addEventListener('scroll', () => closeQuickBuyDropdowns(document), { passive: true });

const initializeAll = (scope = document) => {
  if (!scope) return;
  if (scope.matches?.('[data-quick-buy]')) initialize(scope);
  scope.querySelectorAll?.('[data-quick-buy]').forEach(initialize);

  const standaloneCards = [
    ...(scope.matches?.('[data-quick-buy-card]') ? [scope] : []),
    ...(scope.querySelectorAll?.('[data-quick-buy-card]') || []),
  ].filter((card) => !card.closest('[data-quick-buy]'));

  standaloneCards.forEach(initializeQuickBuyCard);
};

const rootsWithin = (scope) => {
  if (!(scope instanceof Element)) return [];
  return [
    ...(scope.matches('[data-quick-buy]') ? [scope] : []),
    ...scope.querySelectorAll('[data-quick-buy]'),
  ];
};

const dispose = (root) => {
  cleanupByRoot.get(root)?.();
  cleanupByRoot.delete(root);
  instances.delete(root);
};

initializeAll();
document.addEventListener('shopify:section:load', (event) => initializeAll(event.target));
document.addEventListener('shopify:section:unload', (event) => {
  rootsWithin(event.target).forEach(dispose);
  disposeQuickBuyCards(event.target);
});
document.addEventListener('sigma:collection-view:updating', (event) => {
  const view = event.detail?.collection?.querySelector('[data-collection-view]');
  rootsWithin(view).forEach(dispose);
  disposeQuickBuyCards(view);
});
document.addEventListener('sigma:collection-view:updated', (event) => {
  const view = event.detail?.view;
  initializeAll(view);
});
